import React, { PureComponent, ReactNode } from 'react'
import { DispatchProp } from 'react-redux'
import { connectRedux } from '../../../helpers/connectRedux'
import { RouteComponentProps, withRouter } from '../../../helpers/withRouter'

import { Button, Page, Title, ValidatedInput } from '../../../components'
import CountryPickerModal from '../../../containers/CountryPickerModal'
import { images } from '../../../images'
import { isEmailValid, isPasswordValid, isTelephoneValid } from '../../../helpers'
import { ICountry } from '../../../models/country'
import countries from '../../../countries.json'
import { urls } from '../../../routing'
import { LiteralsService } from '../../../services'
import {
  setSignUpCallingCodeAction,
  setSignUpCountryAction,
  setSignUpEmailAction,
  setSignUpPasswordAction,
  setSignUpPhoneAction
} from '../../../store/actions'
import { IStoreState } from '../../../store/states'
import StyledArticle from '../styles'
import { appsLogosSelected, appsStyleSelected, appsUsernameSelected } from '../../../helpers/app'
import { AnyAction } from 'redux'

// Brands that register by phone ask for it instead of the email, as the app does.
const isPhoneSignUp = appsUsernameSelected?.username === 'phone'

interface IProps extends DispatchProp<AnyAction>, RouteComponentProps {
  email: string
  password: string
  phone: string
  country?: ICountry
}

interface IState {
  repeatEmail: string
  repeatPassword: string
}

class SignUpStep1 extends PureComponent<IProps, IState> {
  private get buttonIsDisabled(): boolean {
    const { email, password, phone } = this.props
    const { repeatEmail, repeatPassword } =  this.state
    // No repeat field for the phone, matching the app.
    const isUsernameInvalid = isPhoneSignUp ? !isTelephoneValid(phone) : !isEmailValid(email) || email !== repeatEmail

    return (
      isUsernameInvalid ||
      !isPasswordValid(password) ||
      !isPasswordValid(repeatPassword) ||
      password !== repeatPassword
    )
  }

  private get passwordAlert(): ReactNode {
    const { password } = this.props

    return (
      password !== '' &&
      !isPasswordValid(password) && (
        <div className="error-container">
          <span className="error">
            { LiteralsService.get('validPassword', true) }
          </span>
        </div>
      )
    )
  }

  constructor(props: IProps) {
    super(props)
    this.state = { repeatEmail: '', repeatPassword: '' }

    if (isPhoneSignUp && !props.country) {
      const defaultCountry = countries.find(country => country.code === 'ES')
      if (defaultCountry) {
        props.dispatch(setSignUpCountryAction(defaultCountry))
        props.dispatch(setSignUpCallingCodeAction(defaultCountry.dial_code))
      }
    }
  }

  private get usernameFields(): ReactNode {
    const { email, phone, country } = this.props
    const { repeatEmail } = this.state

    if (isPhoneSignUp) {
      return (
        <ValidatedInput
          name="phone"
          phone
          label={ LiteralsService.get('phone', true) }
          value={ phone }
          country={ country }
          onChange={ this.onPhoneChange }
          error={ !isTelephoneValid(phone) }
        />
      )
    }

    return (
      <>
        <ValidatedInput
          name="email"
          label={ LiteralsService.get('email', true) }
          value={ email }
          onChange={ this.onEmailChange }
          error={ !isEmailValid(email) }
        />
        <ValidatedInput
          name="repeat-email"
          label={ LiteralsService.get('repeatEmail', true) }
          value={ repeatEmail }
          onChange={ this.onRepeatEmailChange }
          error={ !isEmailValid(repeatEmail) || email !== repeatEmail }
        />
      </>
    )
  }

  public render(): ReactNode {

    const { password } = this.props
    const { repeatPassword } = this.state

    return (
      <Page className={ appsStyleSelected?.style }>
        <StyledArticle>
          <form onSubmit={ e => e.preventDefault() }>
            <img src={ images['goBack'] } className="backButton" onClick={ this.handleGoBack } alt="" />
            <header>
              <img  className={ appsLogosSelected?.classRounded }
                    src={ appsLogosSelected?.logoRounded }
                    alt={ appsLogosSelected?.alt } />
            </header>
            <Title text={ LiteralsService.get('yourUserData', true) } />
            <section>
              { this.usernameFields }
              <ValidatedInput
                name="password"
                label={ LiteralsService.get('password', true) }
                value={ password }
                onChange={ this.onPasswordChange }
                error={ !isPasswordValid(password) }
                isPassword
              />
              { this.passwordAlert }
              <ValidatedInput
                name="repeat-password"
                label={ LiteralsService.get('repeatPassword', true) }
                value={ repeatPassword }
                onChange={ this.onRepeatPasswordChange }
                error={ !isPasswordValid(repeatPassword) || password !== repeatPassword }
                isPassword
              />
            </section>
            <section className="actions">
              <Button
                title={ LiteralsService.get('continue') }
                onPress={ this.goToStep2 }
                filled
                upper
                isDisabled={ this.buttonIsDisabled }
              />
              <div className="link">
                <span onClick={ this.goToSignIn }>
                  { LiteralsService.get('alreadyHaveAccount', true) }
                </span>
              </div>
            </section>
            { isPhoneSignUp && <CountryPickerModal selectCountry={ this.onCountryChange } /> }
          </form>
        </StyledArticle>
      </Page>
    )
  }

  private onEmailChange = (email: string) => {
    const { dispatch } = this.props
    dispatch(setSignUpEmailAction(email))
  }

  private onPhoneChange = (phone: string) => {
    this.props.dispatch(setSignUpPhoneAction(phone))
  }

  private onCountryChange = (country: ICountry) => {
    const { dispatch } = this.props
    dispatch(setSignUpCountryAction(country))
    dispatch(setSignUpCallingCodeAction(country.dial_code))
  }

  private onPasswordChange = (password: string) => {
    const { dispatch } = this.props
    dispatch(setSignUpPasswordAction(password))
  }

  private onRepeatEmailChange = (repeatEmail: string) => {
    this.setState({ repeatEmail })
  }

  private onRepeatPasswordChange = (repeatPassword: string) => {
    this.setState({ repeatPassword })
  }

  private goToStep2 = () => {
    this.props.history.push(urls.signUpStep2)
  }

  private goToSignIn = () => {
    this.props.history.push(urls.signIn)
  }

  private handleGoBack = (): void => {
    history.back()
  }
}

const mapStateToProps = ({ signUp }: IStoreState) => ({
  email: signUp.email,
  password: signUp.password,
  phone: signUp.phone,
  country: signUp.country
})

export default withRouter(connectRedux(mapStateToProps, SignUpStep1))
